import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DsButton from '../../common/DsButton';
import DsSelect from '../../common/DsSelect';
import ExplanationButton from '../../common/ExplanationButton';
import { useToast } from '../../common/ToastSystem';
import {
  formatDateTimeJST,
  isoToDatetimeLocalJST,
  jstDatetimeLocalToUtcIso,
} from '../../../utils/datetime';
import {
  EventPlayguideOption,
  EventPlayguideSource,
  EventTicketOption,
  TeketEventSearchResult,
  TeketSetupPreview,
  createTeketEventSource,
  deleteEventPlayguideSource,
  listAvailablePlayguideOptions,
  listEventPlayguideOptions,
  listEventPlayguideSources,
  listEventTicketOptions,
  previewAvailableTeketEventSource,
  previewTeketEventSource,
  searchAvailableTeketEventCandidates,
  searchTeketEventCandidates,
  syncEventPlayguideSources,
  updateEventPlayguideSourceSchedule,
  updateEventPlayguideSourceLinkState,
  updateEventPlayguideTicketMappings,
} from '../../../api/playguides';
import { useConfirm } from '../../../design-system/ConfirmDialog';
import '../../../css/admin/events/PlayguideTicketSourcePanel.css';
import { PLAYGUIDE_SETUP_SECTION_ID } from '../../../utils/eventTicketSalesNavigation';
import PlayguideTicketSyncModal from './PlayguideTicketSyncModal';
import EplusTicketLinkPanel, { EplusSourceDraft } from './EplusTicketLinkPanel';
import PlayguideGuidanceNotice from '../../common/playguides/PlayguideGuidanceNotice';

type ToastApi = { success: (message: string) => void; error: (message: string) => void };
const Select = DsSelect as unknown as React.ComponentType<any>;

export type PlayguideSourceDraft = {
  organizationId: number;
  externalEventId: string;
  capacities: Array<{ externalTicketKey: string; capacity: number }>;
  ticketMappings: Array<{
    externalTicketKey: string;
    casticartTicketId?: number | null;
    targetTicketIndex?: number | null;
    mappingAction?: 'map' | 'create' | 'defer';
    eventCapacityMode?: 'keep' | 'source' | 'manual';
    eventCapacity?: number;
    targetPerformanceMode?: 'auto' | 'event' | 'performance';
    casticartPerformanceId?: number | null;
    targetPerformanceIndex?: number | null;
  }>;
  salesScheduleMode: 'event' | 'custom';
  salesStartAt: string | null;
  salesEndAt: string | null;
};

type DraftTicketOption = {
  ticketId?: number;
  ticketIndex?: number;
  ticketType: string;
  price: number;
  quantity: number;
  eventPerformanceId?: number | null;
  eventPerformanceIndex?: number | null;
  performanceName?: string | null;
  performanceDate?: string | null;
  performanceStartTime?: string | null;
};

type Props = {
  eventUuid?: string;
  eventName: string;
  canEdit: boolean;
  variant?: 'sync' | 'settings';
  eventSalesStartAt?: string;
  eventSalesEndAt?: string;
  eventSalesStartDate?: string;
  eventSalesEndDate?: string;
  ticketOptions?: DraftTicketOption[];
  eventPerformances?: Array<{
    performanceId?: number;
    performanceIndex: number;
    performanceName: string;
    performanceDate: string;
    startTime?: string;
  }>;
  performanceScoped?: boolean;
  onDraftChange?: (draft: PlayguideSourceDraft | null) => void;
  onEplusDraftChange?: (draft: EplusSourceDraft | null) => void;
  onSalesUpdated?: () => void | Promise<void>;
  onConfigurationChanged?: () => void | Promise<void>;
  embedded?: boolean;
  guideFilter?: 'teket' | 'eplus' | 'pia' | 'lawson';
  existingSourcesOnly?: boolean;
};

const SOURCE_STATUS_LABEL: Record<EventPlayguideSource['sourceStatus'], string> = {
  draft: '設定中',
  ready: '連動中',
  syncing: '取得中',
  attention: '確認が必要',
  disabled: '停止中',
};

const yen = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' });
const isReceptionScopedGuide = (guideCode: EventPlayguideSource['guideCode']) =>
  guideCode === 'eplus' || guideCode === 'pia' || guideCode === 'lawson';
const normalizeIdentity = (value: unknown) => String(value || '')
  .normalize('NFKC')
  .replace(/[\s\u3000・･/_-]+/g, '')
  .toLocaleLowerCase('ja-JP');
const receptionScopedTicketName = (ticketName: unknown, receptionName: unknown) => {
  const ticket = String(ticketName || '').normalize('NFKC').trim() || '券種';
  const reception = String(receptionName || '').normalize('NFKC').trim();
  return reception && normalizeIdentity(ticket) !== normalizeIdentity(reception)
    ? `${ticket}（${reception}）`
    : ticket;
};

const sourceCompletion = (source: EventPlayguideSource) => {
  if (source.sourceStatus === 'disabled') {
    return { state: 'is-stopped', title: '連動停止中', body: '過去の券売記録は保持されています。再開する場合は「再連動」を選んでください。' };
  }
  if (source.sourceStatus === 'attention' || Number(source.unmappedTicketCount || 0) > 0) {
    return { state: 'is-attention', title: '確認が必要', body: '未紐付け券種または取得エラーを確認すると、自動反映を完了できます。' };
  }
  if (source.sourceStatus === 'syncing') {
    return { state: 'is-progress', title: '取得中', body: '取得が完了すると券売状況へ反映します。' };
  }
  if (source.sourceStatus === 'ready') {
    return { state: 'is-complete', title: '連動設定完了', body: '券売期間中は自動取得し、取得済みの券売情報をこのイベントへ反映します。' };
  }
  return { state: 'is-progress', title: '設定途中', body: '公演と券種の紐付けを確認して保存してください。' };
};

const extractTeketEventId = (value: unknown): string => {
  const text = String(value || '').trim();
  if (/^\d+$/.test(text)) return text;
  try {
    const url = new URL(text);
    if (url.hostname !== 'teket.jp' && !url.hostname.endsWith('.teket.jp')) return '';
    const numericParts = url.pathname
      .split('/')
      .filter((part) => /^\d+$/.test(part));
    return numericParts[numericParts.length - 1] || '';
  } catch (_error) {
    return '';
  }
};

export default function PlayguideTicketSourcePanel({
  eventUuid,
  eventName,
  canEdit,
  variant = 'sync',
  eventSalesStartAt = '',
  eventSalesEndAt = '',
  eventSalesStartDate = '',
  eventSalesEndDate = '',
  ticketOptions: suppliedTicketOptions,
  eventPerformances = [],
  performanceScoped = false,
  onDraftChange,
  onEplusDraftChange,
  onSalesUpdated,
  onConfigurationChanged,
  embedded = false,
  guideFilter,
  existingSourcesOnly = false,
}: Props) {
  const toast = useToast() as unknown as ToastApi;
  const confirmDialog = useConfirm();
  const settingsMode = variant === 'settings';
  const effectiveEventSalesStartAt = eventSalesStartAt || (eventSalesStartDate ? `${eventSalesStartDate}T00:00:00+09:00` : '');
  const effectiveEventSalesEndAt = eventSalesEndAt || (eventSalesEndDate ? `${eventSalesEndDate}T23:59:59+09:00` : '');
  const [sources, setSources] = useState<EventPlayguideSource[]>([]);
  const [options, setOptions] = useState<EventPlayguideOption[]>([]);
  const [eventTickets, setEventTickets] = useState<DraftTicketOption[]>(suppliedTicketOptions || []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [externalEventId, setExternalEventId] = useState('');
  const [eventSearch, setEventSearch] = useState<TeketEventSearchResult | null>(null);
  const [preview, setPreview] = useState<TeketSetupPreview | null>(null);
  const [capacities, setCapacities] = useState<Record<string, string>>({});
  const [mappingTargets, setMappingTargets] = useState<Record<string, string>>({});
  const [teketPerformanceTargets, setTeketPerformanceTargets] = useState<Record<string, string>>({});
  const [eventCapacityModes, setEventCapacityModes] = useState<Record<string, 'keep' | 'source' | 'manual'>>({});
  const [manualEventCapacities, setManualEventCapacities] = useState<Record<string, string>>({});
  const [sourceMappingTargets, setSourceMappingTargets] = useState<Record<number, Record<string, string>>>({});
  const [sourcePerformanceTargets, setSourcePerformanceTargets] = useState<Record<number, Record<string, string>>>({});
  const [sourceManualCapacities, setSourceManualCapacities] = useState<Record<number, Record<string, string>>>({});
  const [salesScheduleMode, setSalesScheduleMode] = useState<'event' | 'custom'>('event');
  const [salesStartAt, setSalesStartAt] = useState('');
  const [salesEndAt, setSalesEndAt] = useState('');
  const [sourceSchedules, setSourceSchedules] = useState<Record<number, {
    mode: 'event' | 'custom'; startAt: string; endAt: string;
  }>>({});
  const [busyKey, setBusyKey] = useState('');
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [mappingTargetSourceId, setMappingTargetSourceId] = useState<number | null>(null);
  const [expandedCapacity, setExpandedCapacity] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (suppliedTicketOptions) setEventTickets(suppliedTicketOptions);
  }, [suppliedTicketOptions]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const nextSources = eventUuid ? await listEventPlayguideSources(eventUuid) : [];
      const [nextOptions, nextTickets] = settingsMode && canEdit
        ? await Promise.all([
            eventUuid ? listEventPlayguideOptions(eventUuid) : listAvailablePlayguideOptions(),
            suppliedTicketOptions
              ? Promise.resolve(suppliedTicketOptions)
              : eventUuid
                ? listEventTicketOptions(eventUuid)
                : Promise.resolve([]),
          ])
        : [[], suppliedTicketOptions || []];
      setSources(nextSources);
      setOptions(nextOptions.filter((option) => option.guideCode === 'teket'));
      setEventTickets(nextTickets);
      setOrganizationId((current) => current || String(nextOptions[0]?.organizationId || ''));
      setSourceSchedules(Object.fromEntries(nextSources.map((source) => [source.sourceId, {
        mode: source.salesScheduleMode,
        startAt: isoToDatetimeLocalJST(source.salesStartAt) || '',
        endAt: isoToDatetimeLocalJST(source.salesEndAt) || '',
      }])));
      setSourceMappingTargets(Object.fromEntries(nextSources.map((source) => [source.sourceId,
        Object.fromEntries(source.tickets.map((ticket) => [
          ticket.externalTicketKey,
          ticket.casticartTicketId ? String(ticket.casticartTicketId) : '',
        ]))
      ])));
      setSourcePerformanceTargets(Object.fromEntries(nextSources.map((source) => [source.sourceId,
        Object.fromEntries(source.tickets.map((ticket) => [
          ticket.externalTicketKey,
          ticket.casticartPerformanceId == null ? 'event' : `id:${ticket.casticartPerformanceId}`,
        ]))
      ])));
      setSourceManualCapacities(Object.fromEntries(nextSources.map((source) => [source.sourceId,
        Object.fromEntries(source.tickets.map((ticket) => [
          ticket.externalTicketKey,
          source.guideCode === 'teket' &&
          ticket.sourcePlannedCount == null &&
          !ticket.casticartTicketId
            ? ''
            : String(ticket.configuredCapacity),
        ]))
      ])));
    } catch (error: any) {
      setLoadError(error?.message || '自動入力設定を取得できませんでした。');
    } finally {
      setLoading(false);
    }
  }, [canEdit, eventUuid, settingsMode, suppliedTicketOptions]);

  useEffect(() => { void load(); }, [load]);

  const capacityIsValid = useMemo(() => Boolean(preview?.ticketCandidates.length) &&
    preview!.ticketCandidates.every((ticket) => {
      const value = capacities[ticket.externalTicketKey];
      const parsed = Number(value);
      return value !== '' && Number.isSafeInteger(parsed) && parsed >= ticket.soldCount;
    }), [capacities, preview]);
  const selectedOrganization = useMemo(
    () => options.find((option) => String(option.organizationId) === organizationId) || null,
    [options, organizationId]
  );
  const resolvedPerformanceOptions = useMemo(() => {
    const byId = new Map<number, { id: number; name: string; date: string; startTime: string }>();
    eventPerformances.forEach((performance) => {
      if (performance.performanceId) byId.set(performance.performanceId, {
        id: performance.performanceId,
        name: performance.performanceName,
        date: performance.performanceDate,
        startTime: performance.startTime || '',
      });
    });
    eventTickets.forEach((ticket) => {
      if (ticket.eventPerformanceId) byId.set(ticket.eventPerformanceId, {
        id: ticket.eventPerformanceId,
        name: ticket.performanceName || `公演${byId.size + 1}`,
        date: ticket.performanceDate || '',
        startTime: ticket.performanceStartTime || '',
      });
    });
    return Array.from(byId.values()).sort((left, right) =>
      left.date.localeCompare(right.date) || left.startTime.localeCompare(right.startTime) || left.id - right.id
    );
  }, [eventPerformances, eventTickets]);

  const inferTeketPerformanceTarget = useCallback((ticket: TeketSetupPreview['ticketCandidates'][number]) => {
    if (!performanceScoped) {
      if (resolvedPerformanceOptions.length === 1) return `id:${resolvedPerformanceOptions[0].id}`;
      return 'event';
    }
    const exact = eventPerformances.filter((performance) =>
      `${performance.performanceDate}T${performance.startTime || ''}`.slice(0, 16) ===
      String(ticket.performanceDateTime || '').slice(0, 16)
    );
    if (exact.length !== 1) return '';
    return exact[0].performanceId
      ? `id:${exact[0].performanceId}`
      : `index:${exact[0].performanceIndex}`;
  }, [eventPerformances, performanceScoped, resolvedPerformanceOptions]);

  const inferTarget = useCallback((ticket: TeketSetupPreview['ticketCandidates'][number], scopeKey?: string) => {
    const resolvedScope = scopeKey || inferTeketPerformanceTarget(ticket);
    const matches = eventTickets.filter((candidate) =>
      (resolvedScope === 'event'
        ? candidate.eventPerformanceId == null && candidate.eventPerformanceIndex == null
        : resolvedScope.startsWith('id:')
          ? Number(candidate.eventPerformanceId || 0) === Number(resolvedScope.slice(3))
          : Number(candidate.eventPerformanceIndex ?? -1) === Number(resolvedScope.slice(6))) &&
      normalizeIdentity(candidate.ticketType) === normalizeIdentity(ticket.ticketName) &&
      Number(candidate.price) === Number(ticket.unitPrice)
    );
    if (matches.length !== 1) return '';
    return matches[0].ticketId
      ? `id:${matches[0].ticketId}`
      : `index:${matches[0].ticketIndex}`;
  }, [eventTickets, inferTeketPerformanceTarget]);

  const handlePreview = async () => {
    const resolvedExternalEventId = extractTeketEventId(externalEventId);
    if (!organizationId || !resolvedExternalEventId) {
      toast.error('団体とTeketイベントID、またはチケット販売ページURLを入力してください。');
      return;
    }
    setBusyKey('preview');
    try {
      const input = { organizationId: Number(organizationId), externalEventId: resolvedExternalEventId };
      const nextPreview = eventUuid
        ? await previewTeketEventSource(eventUuid, input)
        : await previewAvailableTeketEventSource(input);
      setPreview(nextPreview);
      setExpandedCapacity({});
      setExternalEventId(resolvedExternalEventId);
      setCapacities(Object.fromEntries(nextPreview.ticketCandidates.map((ticket) => [
        ticket.externalTicketKey,
        ticket.sourcePlannedCount == null ? '' : String(ticket.sourcePlannedCount),
      ])));
      setMappingTargets(Object.fromEntries(nextPreview.ticketCandidates.map((ticket) => [
        ticket.externalTicketKey,
        inferTarget(ticket, inferTeketPerformanceTarget(ticket)) || 'create',
      ])));
      setTeketPerformanceTargets(Object.fromEntries(nextPreview.ticketCandidates.map((ticket) => [
        ticket.externalTicketKey,
        inferTeketPerformanceTarget(ticket) || (performanceScoped ? '' : 'event'),
      ])));
      setEventCapacityModes(Object.fromEntries(nextPreview.ticketCandidates.map((ticket) => [
        ticket.externalTicketKey,
        inferTarget(ticket, inferTeketPerformanceTarget(ticket))
          ? 'keep'
          : ticket.sourcePlannedCount == null
            ? 'manual'
            : 'source',
      ])));
      setManualEventCapacities(Object.fromEntries(nextPreview.ticketCandidates.map((ticket) => [
        ticket.externalTicketKey,
        ticket.sourcePlannedCount == null ? '' : String(ticket.sourcePlannedCount),
      ])));
      toast.success('Teketから券種を取得しました');
    } catch (error: any) {
      toast.error(error?.message || 'Teketから券種を取得できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const handleEventSearch = async () => {
    if (!organizationId || !eventName.trim()) {
      toast.error('団体とイベント名を入力してください。');
      return;
    }
    setBusyKey('event-search');
    try {
      const result = eventUuid
        ? await searchTeketEventCandidates(eventUuid, Number(organizationId))
        : await searchAvailableTeketEventCandidates(eventName, Number(organizationId));
      setEventSearch(result);
      const best = result.candidates[0];
      if (!best) {
        toast.error('一致するイベント候補がありません。イベントIDを直接入力できます。');
      } else {
        setExternalEventId(best.externalEventId);
        toast.success('最も近いイベント候補を選択しました');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Teketのイベント候補を取得できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const buildDraft = (): PlayguideSourceDraft | null => {
    if (!preview || !capacityIsValid || (performanceScoped && preview.ticketCandidates.some(
      (ticket) => !teketPerformanceTargets[ticket.externalTicketKey]
    ))) return null;
    return {
      organizationId: preview.organizationId,
      externalEventId: preview.externalEventId,
      capacities: preview.ticketCandidates.map((ticket) => ({
        externalTicketKey: ticket.externalTicketKey,
        capacity: Number(capacities[ticket.externalTicketKey]),
      })),
      ticketMappings: preview.ticketCandidates.map((ticket) => {
        const target = mappingTargets[ticket.externalTicketKey] || '';
        const mappingAction = target === 'create'
          ? 'create'
          : target
          ? 'map'
          : 'defer';
        const eventCapacityMode = eventCapacityModes[ticket.externalTicketKey] ||
          (mappingAction === 'create'
            ? ticket.sourcePlannedCount == null ? 'manual' : 'source'
            : 'keep');
        const performanceTarget = teketPerformanceTargets[ticket.externalTicketKey] || 'event';
        return {
          externalTicketKey: ticket.externalTicketKey,
          casticartTicketId: target.startsWith('id:') ? Number(target.slice(3)) : null,
          targetTicketIndex: target.startsWith('index:') ? Number(target.slice(6)) : null,
          mappingAction,
          eventCapacityMode,
          eventCapacity: eventCapacityMode === 'manual'
            ? Number(manualEventCapacities[ticket.externalTicketKey])
            : undefined,
          targetPerformanceMode: performanceTarget === 'event' ? 'event' as const : 'performance' as const,
          casticartPerformanceId: performanceTarget.startsWith('id:')
            ? Number(performanceTarget.slice(3))
            : null,
          targetPerformanceIndex: performanceTarget.startsWith('index:')
            ? Number(performanceTarget.slice(6))
            : null,
        };
      }),
      salesScheduleMode,
      salesStartAt: salesScheduleMode === 'custom' ? jstDatetimeLocalToUtcIso(salesStartAt) || null : null,
      salesEndAt: salesScheduleMode === 'custom' ? jstDatetimeLocalToUtcIso(salesEndAt) || null : null,
    };
  };

  const handleFinalize = async () => {
    const draft = buildDraft();
    if (!draft) {
      toast.error(performanceScoped && preview?.ticketCandidates.some(
        (ticket) => !teketPerformanceTargets[ticket.externalTicketKey]
      ) ? '対象公演を判定できない券種があります。各券種の対象公演を選択してください。' : 'すべての券種に販売済み枚数以上の上限を入力してください。');
      return;
    }
    const soldCountByKey = new Map(
      preview?.ticketCandidates.map((ticket) => [ticket.externalTicketKey, ticket.soldCount]) || []
    );
    const invalidManualCapacity = draft.ticketMappings.some((mapping) => (
      mapping.eventCapacityMode === 'manual' && (
        !Number.isSafeInteger(mapping.eventCapacity) ||
        Number(mapping.eventCapacity) < Number(soldCountByKey.get(mapping.externalTicketKey) || 0)
      )
    ));
    if (invalidManualCapacity) {
      toast.error('イベント全体へ反映する上限枚数を入力してください。');
      return;
    }
    if (salesScheduleMode === 'event' && (!effectiveEventSalesStartAt || !effectiveEventSalesEndAt)) {
      toast.error('イベント共通の券売開始日時と終了日時を設定してください。');
      return;
    }
    if (salesScheduleMode === 'custom' && (!draft.salesStartAt || !draft.salesEndAt || draft.salesStartAt > draft.salesEndAt)) {
      toast.error('個別の券売開始日時と終了日時を正しく入力してください。');
      return;
    }
    if (!eventUuid) {
      onDraftChange?.(draft);
      toast.success('イベント登録時に保存する自動入力設定へ追加しました');
      return;
    }
    setBusyKey('finalize');
    try {
      const result = await createTeketEventSource(eventUuid, {
        ...draft,
        ticketMappings: draft.ticketMappings.map((mapping) => ({
          ...mapping,
          casticartTicketId: mapping.casticartTicketId || null,
          targetTicketIndex: undefined,
          targetPerformanceIndex: undefined,
        })),
      });
      setPreview(null);
      setExternalEventId('');
      if (result.initialSync.runStatus === 'succeeded') {
        toast.success('Teketを連携し、それまでの券売情報を反映しました');
      } else if (result.initialSync.skipped) {
        toast.success('Teketを連携しました。券売期間中は自動取得または手動取得できます');
      } else {
        toast.error('Teketの連携は保存しましたが、初回の券売情報を反映できませんでした。券売状況取得から再実行してください。');
      }
      await load();
      await onConfigurationChanged?.();
      if (result.initialSync.runStatus === 'succeeded') {
        await onSalesUpdated?.();
      }
    } catch (error: any) {
      toast.error(error?.message || 'Teketの自動入力を設定できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const handleSync = async (sourceIds: number[]) => {
    if (!eventUuid) return;
    setBusyKey('sync');
    try {
      const result = await syncEventPlayguideSources(eventUuid, sourceIds);
      if (!result.sourceCount) toast.error('自動取得が設定されたチケットサイトはありません。');
      else if (result.failed || result.quarantined) toast.error(`${result.succeeded}件を反映し、${result.failed + result.quarantined}件は確認待ちです。`);
      else toast.success(`${result.succeeded}サイトから券売状況を取得しました`);
      setSyncModalOpen(false);
      await load();
      await onSalesUpdated?.();
    } catch (error: any) {
      toast.error(error?.message || '券売状況を取得できませんでした。');
      await load().catch(() => undefined);
    } finally {
      setBusyKey('');
    }
  };

  const handleMappingSave = async (source: EventPlayguideSource) => {
    if (!eventUuid) return;
    const invalidManualTeketCapacity = source.guideCode === 'teket' && source.tickets.some((ticket) => {
      const target = sourceMappingTargets[source.sourceId]?.[ticket.externalTicketKey] || '';
      if (!target || ticket.sourcePlannedCount != null) return false;
      const value = sourceManualCapacities[source.sourceId]?.[ticket.externalTicketKey] || '';
      const parsed = Number(value);
      return value === '' || !Number.isSafeInteger(parsed) || parsed < ticket.configuredCapacity;
    });
    if (invalidManualTeketCapacity) {
      toast.error('Teketから上限を取得できない券種は、販売済み枚数以上の上限を入力してください。');
      return;
    }
    setBusyKey(`mapping:${source.sourceId}`);
    try {
      const result = await updateEventPlayguideTicketMappings(eventUuid, source.sourceId,
        source.tickets.map((ticket) => {
          const target = sourceMappingTargets[source.sourceId]?.[ticket.externalTicketKey] || '';
          const performanceTarget = sourcePerformanceTargets[source.sourceId]?.[ticket.externalTicketKey] ||
            (ticket.casticartPerformanceId == null ? 'event' : `id:${ticket.casticartPerformanceId}`);
          const create = target === 'create';
          const requiresManualTeketCapacity =
            source.guideCode === 'teket' && ticket.sourcePlannedCount == null && Boolean(target);
          return {
            externalTicketKey: ticket.externalTicketKey,
            casticartTicketId: !create && target ? Number(target) : null,
            mappingAction: create ? 'create' as const : target ? 'map' as const : 'defer' as const,
            newTicketName: create
              ? receptionScopedTicketName(ticket.ticketName, ticket.subTicketName)
              : undefined,
            eventCapacityMode: requiresManualTeketCapacity
              ? 'manual' as const
              : create ? 'source' as const : undefined,
            eventCapacity: requiresManualTeketCapacity
              ? Number(sourceManualCapacities[source.sourceId]?.[ticket.externalTicketKey])
              : undefined,
            targetPerformanceMode: performanceTarget === 'event' ? 'event' as const : 'performance' as const,
            casticartPerformanceId: performanceTarget.startsWith('id:')
              ? Number(performanceTarget.slice(3))
              : null,
          };
        })
      );
      if (result.reapplyResult?.reapplied) {
        toast.success('外部券種の紐付けを保存し、取得済みデータを反映しました');
      } else if (result.reapplyResult?.reason === 'no_successful_sync') {
        toast.success('外部券種の紐付けを保存しました。券売情報は次回取得時に反映します');
      } else if (result.reapplyResult?.reason === 'sync_running') {
        toast.success('外部券種の紐付けを保存しました。実行中の取得完了後に反映します');
      } else {
        toast.error('紐付けは保存しましたが、取得済み券売情報を再反映できませんでした。');
      }
      await load();
      setMappingTargetSourceId(null);
      await onSalesUpdated?.();
    } catch (error: any) {
      toast.error(error?.message || '券種の紐付けを保存できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const openTicketMapping = (sourceId: number) => {
    setMappingTargetSourceId(sourceId);
    requestAnimationFrame(() => {
      const target = document.getElementById(`playguide-source-${sourceId}-ticket-mappings`);
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      target?.querySelector<HTMLElement>('select')?.focus();
    });
  };

  const handleScheduleSave = async (source: EventPlayguideSource) => {
    if (!eventUuid) return;
    const schedule = sourceSchedules[source.sourceId];
    const startIso = schedule.mode === 'custom' ? jstDatetimeLocalToUtcIso(schedule.startAt) : null;
    const endIso = schedule.mode === 'custom' ? jstDatetimeLocalToUtcIso(schedule.endAt) : null;
    if (schedule.mode === 'custom' && (!startIso || !endIso || startIso > endIso)) {
      toast.error('個別の券売開始日時と終了日時を正しく入力してください。');
      return;
    }
    setBusyKey(`schedule:${source.sourceId}`);
    try {
      await updateEventPlayguideSourceSchedule(eventUuid, source.sourceId, {
        salesScheduleMode: schedule.mode,
        salesStartAt: startIso,
        salesEndAt: endIso,
      });
      toast.success('券売期間を更新しました');
      await load();
    } catch (error: any) {
      toast.error(error?.message || '券売期間を更新できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const handleSourceDelete = async (source: EventPlayguideSource) => {
    if (!eventUuid) return;
    const guideName = source.guideCode === 'eplus'
      ? 'イープラス'
      : source.guideCode === 'pia'
        ? 'チケットぴあ'
        : source.guideCode === 'lawson'
          ? 'ローチケ'
        : 'Teket';
    const confirmed = await confirmDialog({
      message: `${guideName}との連動を解除しますか？すでに反映済みの券売記録は残り、新しい日の自動取得だけが停止します。`,
      danger: false,
    });
    if (!confirmed) return;
    setBusyKey(`delete:${source.sourceId}`);
    try {
      const result = await deleteEventPlayguideSource(eventUuid, source.sourceId);
      toast.success(result.historyPreserved
        ? '連動を解除しました。反映済みの券売記録は保持されています'
        : '未使用の自動取得設定を削除しました');
      await load();
      await onConfigurationChanged?.();
    } catch (error: any) {
      toast.error(error?.message || '自動取得サイトを削除できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const handleSourceReconnect = async (source: EventPlayguideSource) => {
    if (!eventUuid) return;
    setBusyKey(`reconnect:${source.sourceId}`);
    try {
      await updateEventPlayguideSourceLinkState(eventUuid, source.sourceId, true);
      toast.success('自動取得サイトを再連動しました。過去の確定済み記録は変更しません');
      await load();
      await onConfigurationChanged?.();
    } catch (error: any) {
      toast.error(error?.message || '自動取得サイトを再連動できませんでした。');
    } finally {
      setBusyKey('');
    }
  };

  const visibleSources = guideFilter
    ? sources.filter((source) => source.guideCode === guideFilter)
    : sources;
  const unmappedCount = visibleSources.reduce((sum, source) => sum + Number(source.unmappedTicketCount || 0), 0);
  const syncableSources = visibleSources.filter((source) => source.sourceStatus !== 'disabled');

  return (
    <section id={PLAYGUIDE_SETUP_SECTION_ID} className="playguide-ticket-source" aria-labelledby={embedded ? undefined : 'playguide-ticket-source-title'} aria-label={embedded ? '自動取得サイト連携' : undefined}>
      {!embedded ? <div className="playguide-ticket-source__heading">
        <div>
          <h3 id="playguide-ticket-source-title">{settingsMode ? 'チケットサイトを追加' : '自動取得'}</h3>
          <p>{settingsMode
            ? 'イベントのチケットサイトに外部イベントと券種を紐付けます。設定期間中は毎日自動で取得します。'
            : '設定済みのチケットサイトを選び、最新の券売情報を取得します。'}</p>
        </div>
        {!settingsMode && canEdit ? (
          <DsButton
            disabled={loading || !syncableSources.length}
            onClick={() => setSyncModalOpen(true)}
          >
            券売状況取得
          </DsButton>
        ) : null}
      </div> : null}

      {loading ? <p className="playguide-ticket-source__state">読み込んでいます…</p>
        : loadError ? <div className="playguide-ticket-source__state is-error" role="alert"><p>{loadError}</p><DsButton variant="secondary" size="sm" onClick={load}>再読み込み</DsButton></div>
        : <>
          {settingsMode && guideFilter ? (
            <PlayguideGuidanceNotice guideCode={guideFilter} />
          ) : null}
          {unmappedCount > 0 ? <div className="playguide-ticket-source__state is-error" role="alert">
            <p>取得済みのうち{unmappedCount}件の外部券種が未紐付けです。該当する売上はまだ券売状況へ反映していません。</p>
            {settingsMode && canEdit ? <DsButton
              variant="secondary"
              size="sm"
              onClick={() => openTicketMapping(visibleSources.find((source) => Number(source.unmappedTicketCount || 0) > 0)?.sourceId || visibleSources[0]?.sourceId)}
            >未紐付け券種を設定</DsButton> : null}
            {!settingsMode && eventUuid ? <Link to={`/admin/workspaces/event-operations/events/${eventUuid}/edit#${PLAYGUIDE_SETUP_SECTION_ID}`}>未紐付け券種を設定する</Link> : null}
          </div> : null}

          {visibleSources.length ? <div className="playguide-ticket-source__sources">{visibleSources.map((source) => {
            const orderedTickets = [...source.tickets].sort((left, right) =>
              Number(Boolean(left.casticartTicketId)) - Number(Boolean(right.casticartTicketId))
            );
            const completion = sourceCompletion(source);
            return <article key={source.sourceId} className={`playguide-ticket-source__source-card${mappingTargetSourceId === source.sourceId ? ' is-mapping-target' : ''}`}>
            <header><div><span className={`playguide-ticket-source__status is-${source.sourceStatus}`}>{SOURCE_STATUS_LABEL[source.sourceStatus]}</span><h4>{source.guideCode === 'eplus' ? 'イープラス' : source.guideCode === 'pia' ? 'チケットぴあ' : source.guideCode === 'lawson' ? 'ローチケ' : 'Teket'} / {source.organizationLabel}</h4><p>{isReceptionScopedGuide(source.guideCode) ? '取得済み公演と連動' : `イベントID ${source.externalEventId}`}</p></div>{settingsMode && canEdit ? source.sourceStatus === 'disabled' ? <DsButton variant="secondary" size="sm" loading={busyKey === `reconnect:${source.sourceId}`} onClick={() => void handleSourceReconnect(source)}>再連動</DsButton> : <DsButton variant="secondary" size="sm" loading={busyKey === `delete:${source.sourceId}`} disabled={source.sourceStatus === 'syncing'} onClick={() => void handleSourceDelete(source)}>連動を解除</DsButton> : null}</header>
            <PlayguideGuidanceNotice guideCode={source.guideCode} compact />
            <div className={`playguide-ticket-source__completion ${completion.state}`} role="status"><strong>{completion.title}</strong><span>{completion.body}</span></div>
            <dl><div><dt>最終成功</dt><dd>{formatDateTimeJST(source.lastSuccessfulSyncAt) || '未同期'}</dd></div><div><dt>券種</dt><dd>{source.tickets.length}件（未紐付け {Number(source.unmappedTicketCount || 0)}件）</dd></div></dl>
            {settingsMode && canEdit ? <>
              <div id={`playguide-source-${source.sourceId}-ticket-mappings`} className="playguide-ticket-source__mapping-editor">
                <div className="playguide-ticket-source__mapping-heading">
                  <div><strong>券種の紐付け</strong><p>受付ごとの外部券種を既存券種へ付け替えるか、別の券種として作成できます。</p></div>
                  {isReceptionScopedGuide(source.guideCode) ? <small>新規作成名: 券種（受付）</small> : null}
                </div>
                <div className="playguide-ticket-source__ticket-list">{orderedTickets.map((ticket) => {
                  const scopedName = isReceptionScopedGuide(source.guideCode)
                    ? receptionScopedTicketName(ticket.ticketName, ticket.subTicketName)
                    : ticket.ticketName;
                  const selectedTarget = sourceMappingTargets[source.sourceId]?.[ticket.externalTicketKey] || '';
                  const selectedPerformanceTarget = sourcePerformanceTargets[source.sourceId]?.[ticket.externalTicketKey] ||
                    (ticket.casticartPerformanceId == null ? 'event' : `id:${ticket.casticartPerformanceId}`);
                  const scopedEventTickets = eventTickets.filter((item) => item.ticketId && (
                    selectedPerformanceTarget === 'event'
                      ? item.eventPerformanceId == null
                      : Number(item.eventPerformanceId || 0) === Number(selectedPerformanceTarget.slice(3))
                  ));
                  return <label key={ticket.mappingId} className={!ticket.casticartTicketId ? 'is-unmapped' : ''}>
                    <span><strong>{scopedName}</strong><small>{ticket.subTicketName ? `受付: ${ticket.subTicketName} / ` : ''}{yen.format(ticket.unitPrice || 0)}{!ticket.casticartTicketId ? ' / 未紐付け' : ''}</small></span>
                    {resolvedPerformanceOptions.length > 1 ? <Select aria-label={`${scopedName}の対象公演`} value={selectedPerformanceTarget} onChange={(event: any) => {
                      const nextScope = event.target.value;
                      setSourcePerformanceTargets((current) => ({ ...current, [source.sourceId]: { ...current[source.sourceId], [ticket.externalTicketKey]: nextScope } }));
                      setSourceMappingTargets((current) => ({ ...current, [source.sourceId]: { ...current[source.sourceId], [ticket.externalTicketKey]: '' } }));
                    }}>
                      <option value="event">イベント全体（通し券など）</option>
                      {resolvedPerformanceOptions.map((performance) => <option key={performance.id} value={`id:${performance.id}`}>{performance.date} {performance.startTime} / {performance.name}</option>)}
                    </Select> : null}
                    <Select aria-label={`${scopedName}の紐付け先`} value={selectedTarget} onChange={(event: any) => setSourceMappingTargets((current) => ({ ...current, [source.sourceId]: { ...current[source.sourceId], [ticket.externalTicketKey]: event.target.value } }))}>
                      <option value="">未紐付け</option>
                      {isReceptionScopedGuide(source.guideCode) ? <option value="create">新しい券種「{scopedName}」として追加</option> : null}
                      {scopedEventTickets.map((item) => <option key={item.ticketId} value={item.ticketId}>{item.eventPerformanceId == null ? 'イベント全体 / ' : ''}{item.ticketType} / {yen.format(item.price)}</option>)}
                    </Select>
                    {source.guideCode === 'teket' && ticket.sourcePlannedCount == null && selectedTarget ? <span>
                      <input
                        aria-label={`${scopedName}の上限枚数`}
                        type="number"
                        min={ticket.configuredCapacity}
                        placeholder="手動入力"
                        value={sourceManualCapacities[source.sourceId]?.[ticket.externalTicketKey] || ''}
                        onChange={(event) => setSourceManualCapacities((current) => ({
                          ...current,
                          [source.sourceId]: {
                            ...current[source.sourceId],
                            [ticket.externalTicketKey]: event.target.value,
                          },
                        }))}
                      />
                      <small>Teketから上限を取得できないため、販売済み枚数以上で手動入力してください。</small>
                    </span> : null}
                  </label>;
                })}</div>
                {isReceptionScopedGuide(source.guideCode) ? <p className="playguide-ticket-source__mapping-note">紐付け変更は未反映の日から適用します。すでに確定した過去日の券売記録と以前の券種は変更・削除しません。</p> : null}
              </div>
              <DsButton variant="secondary" size="sm" loading={busyKey === `mapping:${source.sourceId}`} onClick={() => handleMappingSave(source)}>券種の紐付けを保存</DsButton>
              {sourceSchedules[source.sourceId] ? <div className="playguide-ticket-source__schedule-editor"><label>券売期間<Select value={sourceSchedules[source.sourceId].mode} onChange={(event: any) => setSourceSchedules((current) => ({ ...current, [source.sourceId]: { ...current[source.sourceId], mode: event.target.value } }))}><option value="event">券売予定に合わせる</option><option value="custom">個別で設定する</option></Select></label>{sourceSchedules[source.sourceId].mode === 'custom' ? <div className="playguide-ticket-source__schedule-dates"><label>開始日時<input type="datetime-local" value={sourceSchedules[source.sourceId].startAt} onChange={(event) => setSourceSchedules((current) => ({ ...current, [source.sourceId]: { ...current[source.sourceId], startAt: event.target.value } }))} /></label><label>終了日時<input type="datetime-local" value={sourceSchedules[source.sourceId].endAt} onChange={(event) => setSourceSchedules((current) => ({ ...current, [source.sourceId]: { ...current[source.sourceId], endAt: event.target.value } }))} /></label></div> : <p>イベント共通: {formatDateTimeJST(effectiveEventSalesStartAt) || '未設定'}〜{formatDateTimeJST(effectiveEventSalesEndAt) || '未設定'}</p>}<DsButton variant="secondary" size="sm" loading={busyKey === `schedule:${source.sourceId}`} onClick={() => handleScheduleSave(source)}>期間を保存</DsButton></div> : null}
            </> : null}
          </article>;
          })}</div> : !settingsMode ? <p className="playguide-ticket-source__state">自動取得が設定されたチケットサイトはありません。</p> : null}

          {settingsMode && canEdit && !existingSourcesOnly && (!guideFilter || guideFilter === 'teket') ? <div className="playguide-ticket-source__setup">
            <div className="playguide-ticket-source__setup-title"><h4>自動取得 / Teket</h4><p>団体とイベントを選び、外部券種を既存券種へ紐付けるか、新しい券種として追加します。</p></div>
            {!options.length ? <p className="playguide-ticket-source__empty">現在利用できるTeket団体はありません。プレイガイドアカウントを管理する担当者へ確認してください。</p> : <>
              <div className="playguide-ticket-source__organization-step"><label>団体<Select value={organizationId} onChange={(event: any) => { setOrganizationId(event.target.value); setEventSearch(null); setPreview(null); }} >{options.map((option) => <option key={option.organizationId} value={option.organizationId}>{option.connectionLabel || 'Teket'} / {option.label}</option>)}</Select>{selectedOrganization ? <small>既定手数料 {Number(selectedOrganization.defaultSiteFee || 0)}%（連携後にイベントごとに変更できます）</small> : null}</label><div className="playguide-ticket-source__event-search"><div><strong>イベント名から候補を検索</strong><p>「{eventName || 'イベント名未設定'}」に近いTeketイベントを探します。</p></div><DsButton variant="secondary" loading={busyKey === 'event-search'} onClick={handleEventSearch}>候補を検索</DsButton></div></div>
              {eventSearch?.candidates.length ? <label>Teketイベント候補<Select value={externalEventId} onChange={(event: any) => { setExternalEventId(event.target.value); setPreview(null); }}>{eventSearch.candidates.map((candidate) => <option key={candidate.externalEventId} value={candidate.externalEventId}>{candidate.name}（ID {candidate.externalEventId}）</option>)}</Select></label> : null}
              <div className="playguide-ticket-source__identity-form"><label><span className="playguide-ticket-source__label-with-help">TeketイベントIDまたはチケット販売ページURL<ExplanationButton title="イベントIDとは？" ariaLabel="TeketイベントIDの説明" body="TeketのイベントページURLの末尾にある数字です。例：https://teket.jp/group/5288/67856 の場合は 67856 です。イベントIDだけを入力するか、URLをそのまま貼り付けられます。">{null}</ExplanationButton></span><input value={externalEventId} placeholder="例：67856 または https://teket.jp/group/5288/67856" onChange={(event) => { setExternalEventId(event.target.value); setPreview(null); }} /></label><DsButton variant="secondary" loading={busyKey === 'preview'} onClick={handlePreview}>券種を読み込む</DsButton></div>
              <div className="playguide-ticket-source__schedule-editor"><label>券売期間<Select value={salesScheduleMode} onChange={(event: any) => setSalesScheduleMode(event.target.value)}><option value="event">券売予定に合わせる</option><option value="custom">個別で設定する</option></Select></label>{salesScheduleMode === 'custom' ? <div className="playguide-ticket-source__schedule-dates"><label>開始日時<input type="datetime-local" value={salesStartAt} onChange={(event) => setSalesStartAt(event.target.value)} /></label><label>終了日時<input type="datetime-local" value={salesEndAt} onChange={(event) => setSalesEndAt(event.target.value)} /></label></div> : <p>イベント共通: {formatDateTimeJST(effectiveEventSalesStartAt) || '未設定'}〜{formatDateTimeJST(effectiveEventSalesEndAt) || '未設定'}</p>}</div>
              {preview ? (() => {
                const autoMatchedCount = preview.ticketCandidates.filter((ticket) => {
                  const value = mappingTargets[ticket.externalTicketKey] || '';
                  return Boolean(value) && value !== 'create';
                }).length;
                const attentionCount = preview.ticketCandidates.filter((ticket) => {
                  const needsPerformance = performanceScoped && eventPerformances.length > 1 && !teketPerformanceTargets[ticket.externalTicketKey];
                  const needsCapacity = ticket.sourcePlannedCount == null && !capacities[ticket.externalTicketKey];
                  return needsPerformance || needsCapacity;
                }).length;
                const orderedCandidates = [...preview.ticketCandidates].sort((left, right) =>
                  Number(left.sourcePlannedCount != null) - Number(right.sourcePlannedCount != null)
                );
                return <div className="playguide-ticket-source__preview">
                <p className="playguide-ticket-source__map-summary" role="status">{autoMatchedCount >= preview.ticketCandidates.length
                  ? `${preview.ticketCandidates.length}件すべてを名称・金額から自動一致しました。`
                  : `${preview.ticketCandidates.length}件中${autoMatchedCount}件を名称・金額から自動一致しました。${attentionCount ? `残り${attentionCount}件を確認してください。` : ''}`}</p>
                <div className="playguide-ticket-source__map-list">{orderedCandidates.map((ticket) => {
                const target = mappingTargets[ticket.externalTicketKey] || '';
                const performanceTarget = teketPerformanceTargets[ticket.externalTicketKey] || '';
                const scopedTickets = eventTickets.filter((item) => performanceTarget === 'event'
                  ? item.eventPerformanceId == null && item.eventPerformanceIndex == null
                  : performanceTarget.startsWith('id:')
                    ? Number(item.eventPerformanceId || 0) === Number(performanceTarget.slice(3))
                    : Number(item.eventPerformanceIndex ?? -1) === Number(performanceTarget.slice(6)));
                const selectedTicket = scopedTickets.find((item) => target === (item.ticketId ? `id:${item.ticketId}` : `index:${item.ticketIndex}`));
                const sourceCapacityUnavailable = ticket.sourcePlannedCount == null;
                const isMapped = Boolean(target) && target !== 'create';
                const needsPerformance = performanceScoped && eventPerformances.length > 1 && !performanceTarget;
                const needsCapacity = sourceCapacityUnavailable && !capacities[ticket.externalTicketKey];
                const attention = needsPerformance || needsCapacity;
                const capacityOpen = sourceCapacityUnavailable || Boolean(expandedCapacity[ticket.externalTicketKey]);
                const eventCapacityMode = eventCapacityModes[ticket.externalTicketKey] || (target === 'create' ? sourceCapacityUnavailable ? 'manual' : 'source' : 'keep');
                const reflectToEvent = isMapped && eventCapacityMode !== 'keep';
                return <article key={ticket.externalTicketKey} className={`playguide-ticket-source__map-card${attention ? ' is-attention' : ''}`}>
                  <div className="playguide-ticket-source__map-card-head"><div><strong>{ticket.displayLabel}</strong>{isMapped ? <span className="playguide-ticket-source__map-tag is-auto">自動一致</span> : null}{attention ? <span className="playguide-ticket-source__map-tag is-attention">要確認</span> : null}</div><small>{yen.format(ticket.unitPrice)}</small></div>
                  <div className="playguide-ticket-source__map-card-fields">
                    {performanceScoped && eventPerformances.length > 1 ? <label>対象公演<Select aria-label={`${ticket.displayLabel}の対象公演`} value={performanceTarget} onChange={(event: any) => { setTeketPerformanceTargets((current) => ({ ...current, [ticket.externalTicketKey]: event.target.value })); setMappingTargets((current) => ({ ...current, [ticket.externalTicketKey]: 'create' })); }}><option value="">選択してください</option><option value="event">イベント全体（通し券など）</option>{eventPerformances.map((performance) => { const value = performance.performanceId ? `id:${performance.performanceId}` : `index:${performance.performanceIndex}`; return <option key={value} value={value}>{performance.performanceDate} {performance.startTime} / {performance.performanceName}</option>; })}</Select></label> : null}
                    <label>対応先<Select aria-label={`${ticket.displayLabel}の対応先`} value={target} onChange={(event: any) => { const nextTarget = event.target.value; setMappingTargets((current) => ({ ...current, [ticket.externalTicketKey]: nextTarget })); setEventCapacityModes((current) => ({ ...current, [ticket.externalTicketKey]: nextTarget === 'create' ? sourceCapacityUnavailable ? 'manual' : 'source' : 'keep' })); }}><option value="">あとで紐付ける</option><option value="create">新しいチケット種別として追加</option>{scopedTickets.map((item) => { const value = item.ticketId ? `id:${item.ticketId}` : `index:${item.ticketIndex}`; return <option key={value} value={value}>{item.ticketType} / {yen.format(item.price)}</option>; })}</Select></label>
                  </div>
                  <div className="playguide-ticket-source__map-card-capacity">{capacityOpen ? <>
                    <label>このサイトの枠<input aria-label={`${ticket.displayLabel}のTeket上限`} type="number" min={ticket.soldCount} placeholder={sourceCapacityUnavailable ? '手動入力' : undefined} value={capacities[ticket.externalTicketKey] || ''} onChange={(event) => { const nextValue = event.target.value; setCapacities((current) => ({ ...current, [ticket.externalTicketKey]: nextValue })); if (sourceCapacityUnavailable) setManualEventCapacities((current) => ({ ...current, [ticket.externalTicketKey]: nextValue })); }} /></label>
                    {sourceCapacityUnavailable ? <small>Teketから上限を取得できないため手動入力</small> : null}
                    {isMapped ? <div className="playguide-ticket-source__map-card-evt"><label className="playguide-ticket-source__checkline"><input type="checkbox" checked={reflectToEvent} onChange={(event) => setEventCapacityModes((current) => ({ ...current, [ticket.externalTicketKey]: event.target.checked ? sourceCapacityUnavailable ? 'manual' : 'source' : 'keep' }))} />イベント全体の上限にも反映する</label>{reflectToEvent ? <><label>反映方法<Select aria-label={`${ticket.displayLabel}の反映方法`} value={eventCapacityMode} onChange={(event: any) => setEventCapacityModes((current) => ({ ...current, [ticket.externalTicketKey]: event.target.value }))}><option value="source" disabled={sourceCapacityUnavailable}>Teket上限を反映{sourceCapacityUnavailable ? '（未取得）' : ''}</option><option value="manual">個別入力</option></Select></label>{eventCapacityMode === 'manual' ? <label>上限枚数<input aria-label={`${ticket.displayLabel}のイベント全体上限`} type="number" min={ticket.soldCount} value={manualEventCapacities[ticket.externalTicketKey] || ''} onChange={(event) => setManualEventCapacities((current) => ({ ...current, [ticket.externalTicketKey]: event.target.value }))} /></label> : null}</> : null}</div> : null}
                  </> : <button type="button" className="playguide-ticket-source__cap-toggle" onClick={() => setExpandedCapacity((current) => ({ ...current, [ticket.externalTicketKey]: true }))}>上限を調整{selectedTicket ? `（現在 ${selectedTicket.quantity}枚）` : ''}</button>}</div>
                </article>;
              })}</div><p className="playguide-ticket-source__capacity-note">「このサイトの枠」はこのサイト専用の値として保存します。イベント全体の上限も変える場合だけ「イベント全体の上限にも反映」を選びます。</p>{preview.ticketCandidates.some((ticket) => ticket.sourcePlannedCount == null) ? <p className="playguide-ticket-source__capacity-note">Teketで上限・残数が空欄の券種は、0枚と推測せず手動入力した上限を使います。</p> : null}{preview.ticketCandidates.some((ticket) => ticket.unattributedSoldCount > 0) ? <p className="playguide-ticket-source__capacity-note">Teketで販売日を特定できない売上は、日別実績へ推測配分せず「自動・対象日なし」として全体実績へ反映します。</p> : null}<div className="playguide-ticket-source__preview-actions"><DsButton variant="secondary" onClick={() => { setPreview(null); onDraftChange?.(null); }}>やり直す</DsButton><DsButton loading={busyKey === 'finalize'} disabled={!capacityIsValid} onClick={handleFinalize}>{eventUuid ? 'このサイトの連動を保存' : '登録内容へ追加'}</DsButton></div></div>;
              })() : null}
            </>}
          </div> : null}
          {settingsMode && canEdit && !existingSourcesOnly && (!guideFilter || guideFilter === 'eplus') && (eventUuid || eventPerformances.length) ? (
            <EplusTicketLinkPanel
              guideCode="eplus"
              eventUuid={eventUuid}
              eventPerformances={eventPerformances}
              performanceScoped={performanceScoped}
              eventTickets={(eventUuid
                ? eventTickets.filter((ticket): ticket is EventTicketOption => Boolean(ticket.ticketId))
                : eventTickets) as Array<EventTicketOption & {
                  ticketIndex?: number;
                  eventPerformanceIndex?: number | null;
                }>}
              onDraftChange={onEplusDraftChange}
              onSaved={async () => {
                await load();
                await onSalesUpdated?.();
              }}
            />
          ) : null}
          {settingsMode && canEdit && !existingSourcesOnly && guideFilter === 'pia' && eventUuid ? (
            <EplusTicketLinkPanel
              guideCode="pia"
              eventUuid={eventUuid}
              eventPerformances={eventPerformances}
              performanceScoped={performanceScoped}
              eventTickets={eventTickets.filter((ticket): ticket is EventTicketOption => Boolean(ticket.ticketId))}
              onSaved={async () => {
                await load();
                await onSalesUpdated?.();
              }}
            />
          ) : null}
          {settingsMode && canEdit && !existingSourcesOnly && guideFilter === 'lawson' && eventUuid ? (
            <EplusTicketLinkPanel
              guideCode="lawson"
              eventUuid={eventUuid}
              eventPerformances={eventPerformances}
              performanceScoped={performanceScoped}
              eventTickets={eventTickets.filter((ticket): ticket is EventTicketOption => Boolean(ticket.ticketId))}
              onSaved={async () => {
                await load();
                await onSalesUpdated?.();
              }}
            />
          ) : null}
          {settingsMode && canEdit && !existingSourcesOnly && guideFilter === 'lawson' && !eventUuid ? (
            <p className="playguide-ticket-source__empty">
              ローチケは登録済みイベントの公演日と取得済み公演を照合して連動します。イベント登録後に設定してください。
            </p>
          ) : null}
        </>}
      {!settingsMode ? (
        <PlayguideTicketSyncModal
          open={syncModalOpen}
          sources={syncableSources}
          loading={busyKey === 'sync'}
          onClose={() => setSyncModalOpen(false)}
          onConfirm={handleSync}
        />
      ) : null}
    </section>
  );
}
